import type {
  ApplicationFileVideoSource,
  CloudflareStreamVideoSource,
  JwPlayerVideoSource,
  LegacyJwVideoOption,
  VideoSource,
} from "#bb/types/blocks/content/VideoOption";

export const CLOUDFLARE_STREAM_VIDEO_UID_METADATA_KEY =
  "cloudflare-stream-video-uid";
export const CLOUDFLARE_STREAM_PROVIDER_METADATA_KEY =
  "cloudflare-stream-provider";

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isApplicationFileVideoSource(
  value: unknown,
): value is ApplicationFileVideoSource {
  return isRecord(value)
    && value.provider === "application-file"
    && isNonEmptyString(value.fileId)
    && isNonEmptyString(value.path);
}

export function isJwPlayerVideoSource(
  value: unknown,
): value is JwPlayerVideoSource {
  return isRecord(value)
    && value.provider === "jwplayer"
    && isNonEmptyString(value.assetId)
    && isNonEmptyString(value.playerId);
}

export function isCloudflareStreamVideoSource(
  value: unknown,
): value is CloudflareStreamVideoSource {
  return isRecord(value)
    && value.provider === "cloudflare-stream"
    && isNonEmptyString(value.assetId);
}

export function isVideoSource(value: unknown): value is VideoSource {
  return isApplicationFileVideoSource(value)
    || isJwPlayerVideoSource(value)
    || isCloudflareStreamVideoSource(value);
}

export function adaptLegacyJwVideoSource(
  value: unknown,
): JwPlayerVideoSource | null {
  if (!isRecord(value)
    || !isNonEmptyString(value.videoId)
    || !isNonEmptyString(value.playerId)) {
    return null;
  }

  const legacy = value as unknown as LegacyJwVideoOption;
  return {
    provider: "jwplayer",
    assetId: legacy.videoId.trim(),
    playerId: legacy.playerId.trim(),
  };
}

export function resolveSelectedFileVideoSource(entry: {
  id?: string;
  path: string;
  metadata?: Record<string, unknown>;
}, storedPath = entry.path): VideoSource {
  const assetId = entry.metadata?.[
    CLOUDFLARE_STREAM_VIDEO_UID_METADATA_KEY
  ];
  const provider = entry.metadata?.[
    CLOUDFLARE_STREAM_PROVIDER_METADATA_KEY
  ];

  if (
    provider === "cloudflare-stream"
    && isNonEmptyString(assetId)
  ) {
    return {
      provider: "cloudflare-stream",
      assetId: assetId.trim(),
      fileId: entry.id?.trim() || storedPath,
      path: storedPath,
    };
  }

  return {
    provider: "application-file",
    fileId: entry.id?.trim() || storedPath,
    path: storedPath,
  };
}

export function buildCloudflareStreamEmbedUrl(
  source: CloudflareStreamVideoSource,
  options: {
    autoplay?: boolean;
    controls?: boolean;
    loop?: boolean;
    muted?: boolean;
    startSeconds?: number;
  } = {},
): string {
  const url = new URL(
    `https://iframe.videodelivery.net/${encodeURIComponent(source.assetId.trim())}`,
  );
  url.searchParams.set("autoplay", options.autoplay ? "true" : "false");
  url.searchParams.set("controls", options.controls === false ? "false" : "true");
  url.searchParams.set("loop", options.loop ? "true" : "false");
  url.searchParams.set(
    "muted",
    options.autoplay || options.muted ? "true" : "false",
  );
  const startSeconds = Number(options.startSeconds);
  if (Number.isFinite(startSeconds) && startSeconds > 0) {
    url.searchParams.set("startTime", String(Math.floor(startSeconds)));
  }
  return url.toString();
}

export function buildJwPlayerEmbedUrl(source: JwPlayerVideoSource): string {
  return `https://cdn.jwplayer.com/players/${encodeURIComponent(source.assetId.trim())}-${encodeURIComponent(source.playerId.trim())}.html`;
}